import {
  RefreshCw,
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  CopyPlus,
  createElement,
  Download,
  Eye,
  EyeOff,
  Layers,
  Lock,
  LockOpen,
  MapPin,
  MousePointer2,
  Pencil,
  TableProperties,
  Trash2,
  Type,
  ZoomIn,
  ZoomOut,
  type IconNode,
} from 'lucide';

const icons = {
  refresh: RefreshCw,
  'arrow-down': ArrowDown,
  'arrow-up': ArrowUp,
  check: Check,
  copy: Copy,
  duplicate: CopyPlus,
  download: Download,
  eye: Eye,
  'eye-off': EyeOff,
  layers: Layers,
  lock: Lock,
  unlock: LockOpen,
  'map-pin': MapPin,
  'mouse-pointer': MousePointer2,
  pencil: Pencil,
  table: TableProperties,
  trash: Trash2,
  type: Type,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
} satisfies Record<string, IconNode>;

export type ContextMenuIcon = keyof typeof icons;

export interface ContextMenuItem<Command extends string = string> {
  command: Command;
  label: string;
  icon: ContextMenuIcon;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  separatorBefore?: boolean;
}

export interface ContextMenuRequest<Command extends string = string> {
  ownerDocument: Document;
  anchor: { x: number; y: number };
  label: string;
  items: ContextMenuItem<Command>[];
  onSelect(command: Command): void;
  onClose?(): void;
}

const controllers = new WeakMap<Document, ContextMenuController>();

class ContextMenuController {
  private root: HTMLDivElement | null = null;
  private onClose: (() => void) | undefined;
  private previousFocus: HTMLElement | null = null;
  private scrollPositions = new Map<EventTarget, { x: number; y: number }>();

  constructor(private readonly ownerDocument: Document) {}

  open<Command extends string>(request: ContextMenuRequest<Command>): () => void {
    const retainedFocus = this.root ? this.previousFocus : null;
    this.close(false);
    const view = this.ownerDocument.defaultView;
    const HTMLElementCtor = view?.HTMLElement;
    const activeElement = this.ownerDocument.activeElement;
    this.previousFocus = retainedFocus ?? (HTMLElementCtor && activeElement instanceof HTMLElementCtor
      ? activeElement
      : null);

    const root = this.ownerDocument.createElement('div');
    root.className = 'app-context-menu tw:absolute tw:z-(--layer-popover) tw:grid tw:min-w-[210px] tw:p-[6px] tw:[border:1px_solid_rgb(15_23_42_/_12%)] tw:rounded-[11px] tw:bg-(--bg-toolbar) tw:[box-shadow:0_16px_40px_rgb(15_23_42_/_20%),0_3px_10px_rgb(15_23_42_/_10%)] tw:text-(--text-primary) tw:[backdrop-filter:blur(14px)] tw:[@media(width<=768px)]:min-w-[200px]';
    root.setAttribute('role', 'menu');
    root.setAttribute('aria-label', request.label);
    root.addEventListener('click', (event) => event.stopPropagation());
    root.addEventListener('contextmenu', (event) => event.preventDefault());

    for (const item of request.items) {
      if (item.separatorBefore) {
        const separator = this.ownerDocument.createElement('div');
        separator.className = 'app-context-menu-separator tw:h-px tw:my-[5px] tw:mx-[4px] tw:bg-(--divider-color)';
        separator.setAttribute('role', 'separator');
        root.appendChild(separator);
      }
      const button = this.ownerDocument.createElement('button');
      button.type = 'button';
      button.className = `app-context-menu-item tw:grid tw:grid-cols-[18px_minmax(0,1fr)_auto] tw:items-center tw:gap-[9px] tw:min-h-[36px] tw:py-[7px] tw:px-[9px] tw:[border:0] tw:rounded-[7px] tw:bg-transparent tw:cursor-pointer tw:font-inherit tw:text-[13px] tw:text-left tw:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:bg-(--bg-hover) tw:focus-visible:bg-(--bg-hover) tw:[@media_(hover:_hover)_and_(pointer:_fine)]:[&:hover]:[outline:none] tw:focus-visible:[outline:none] tw:disabled:cursor-default tw:disabled:opacity-[0.42] tw:[[dir=rtl]_&]:text-right tw:[@media(width<=768px)]:min-h-[42px] ${item.danger ? 'danger tw:text-[#dc2626]' : 'tw:text-inherit'}`;
      button.setAttribute('role', 'menuitem');
      button.disabled = Boolean(item.disabled);
      const icon = createElement(icons[item.icon], {
        width: '16', height: '16', 'stroke-width': '2', 'aria-hidden': 'true',
      });
      icon.classList.add('app-context-menu-icon', 'tw:w-[16px]', 'tw:h-[16px]', 'tw:fill-none', 'tw:stroke-current', 'tw:[stroke-linecap:round]', 'tw:[stroke-linejoin:round]');
      const label = this.ownerDocument.createElement('span');
      label.className = 'app-context-menu-label tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap';
      label.textContent = item.label;
      button.appendChild(icon);
      button.appendChild(label);
      if (item.shortcut) {
        const shortcut = this.ownerDocument.createElement('kbd');
        shortcut.className = 'tw:text-(--text-muted) tw:font-inherit tw:text-[11px]';
        shortcut.textContent = item.shortcut;
        button.appendChild(shortcut);
      }
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (button.disabled) return;
        this.close(true);
        request.onSelect(item.command);
      });
      root.appendChild(button);
    }

    this.ownerDocument.body.appendChild(root);
    this.root = root;
    this.onClose = request.onClose;
    this.position(root, request.anchor);
    this.ownerDocument.addEventListener('pointerdown', this.handleOutsidePointerDown, true);
    this.ownerDocument.addEventListener('keydown', this.handleKeydown, true);
    this.scrollPositions.clear();
    for (let element = this.previousFocus; element; element = element.parentElement) {
      this.scrollPositions.set(element, { x: element.scrollLeft, y: element.scrollTop });
    }
    if (view) this.scrollPositions.set(this.ownerDocument, { x: view.scrollX, y: view.scrollY });
    view?.addEventListener('resize', this.handleViewportChange);
    view?.addEventListener('scroll', this.handleScroll, true);
    root.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
    return () => { if (this.root === root) this.close(false); };
  }

  private position(root: HTMLElement, anchor: { x: number; y: number }): void {
    const view = this.ownerDocument.defaultView;
    const viewportWidth = view?.innerWidth ?? this.ownerDocument.documentElement.clientWidth;
    const viewportHeight = view?.innerHeight ?? this.ownerDocument.documentElement.clientHeight;
    const scrollX = view?.scrollX ?? 0;
    const scrollY = view?.scrollY ?? 0;
    const edge = 8;
    root.style.left = `${anchor.x + scrollX}px`;
    root.style.top = `${anchor.y + scrollY}px`;
    const bounds = root.getBoundingClientRect();
    root.style.left = `${scrollX + Math.max(edge, Math.min(anchor.x, viewportWidth - bounds.width - edge))}px`;
    root.style.top = `${scrollY + Math.max(edge, Math.min(anchor.y, viewportHeight - bounds.height - edge))}px`;
  }

  private menuButtons(): HTMLButtonElement[] {
    return this.root ? [...this.root.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')] : [];
  }

  private handleOutsidePointerDown = (event: PointerEvent): void => {
    const target = event.target;
    const NodeCtor = this.ownerDocument.defaultView?.Node;
    if (!this.root || !NodeCtor || !(target instanceof NodeCtor) || !this.root.contains(target)) this.close(false);
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (!this.root) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const buttons = this.menuButtons();
    if (!buttons.length) return;
    event.preventDefault();
    event.stopPropagation();
    const current = buttons.findIndex((button) => button === this.ownerDocument.activeElement);
    const index = event.key === 'Home' ? 0
      : event.key === 'End' ? buttons.length - 1
        : event.key === 'ArrowDown' ? (current + 1 + buttons.length) % buttons.length
          : (current - 1 + buttons.length) % buttons.length;
    buttons[index]?.focus();
  };

  private handleViewportChange = (): void => this.close(false);

  private handleScroll = (event: Event): void => {
    const target = event.target;
    if (!target) return;
    const position = this.scrollPositions.get(target);
    if (!position) return;
    const view = this.ownerDocument.defaultView;
    const element = target instanceof Element ? target : null;
    const x = element?.scrollLeft ?? view?.scrollX;
    const y = element?.scrollTop ?? view?.scrollY;
    // A queued scroll from revealing the opener does not move the new menu.
    if (x !== position.x || y !== position.y) this.close(false);
  };

  private close(restoreFocus: boolean): void {
    if (!this.root) return;
    this.root.remove();
    this.root = null;
    this.ownerDocument.removeEventListener('pointerdown', this.handleOutsidePointerDown, true);
    this.ownerDocument.removeEventListener('keydown', this.handleKeydown, true);
    const view = this.ownerDocument.defaultView;
    view?.removeEventListener('resize', this.handleViewportChange);
    view?.removeEventListener('scroll', this.handleScroll, true);
    this.scrollPositions.clear();
    if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus();
    this.previousFocus = null;
    const onClose = this.onClose;
    this.onClose = undefined;
    onClose?.();
  }
}

export function showContextMenu<Command extends string>(request: ContextMenuRequest<Command>): () => void {
  let controller = controllers.get(request.ownerDocument);
  if (!controller) {
    controller = new ContextMenuController(request.ownerDocument);
    controllers.set(request.ownerDocument, controller);
  }
  return controller.open(request);
}
